/* <symbol-explorer> — framed coin "Symbols" explorer.
   Server-side markup (snippets/symbol-explorer.liquid) holds the face toggle,
   all symbol tabs (tagged data-face) and all detail panels. This element
   handles the Obverse/Reverse toggle, per-face tab visibility, the sliding
   marker/thumb, the count and panel switching. */
class SymbolExplorer extends HTMLElement {
  connectedCallback() {
    if (this._init) return;
    this._init = true;
    this.reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.faceBtns = Array.prototype.slice.call(this.querySelectorAll('.symbol-explorer__faces button'));
    this.thumb = this.querySelector('.symbol-explorer__thumb');
    this.count = this.querySelector('.symbol-explorer__count');
    this.row = this.querySelector('.symbol-explorer__track-row');
    this.track = this.querySelector('.symbol-explorer__track');
    this.prev = this.querySelector('.symbol-explorer__prev');
    this.next = this.querySelector('.symbol-explorer__next');
    this.marker = this.querySelector('.symbol-explorer__marker');
    this.allTabs = Array.prototype.slice.call(this.querySelectorAll('.symbol-explorer__tab'));
    this.allPanels = Array.prototype.slice.call(this.querySelectorAll('.symbol-explorer__detail'));
    if (!this.allTabs.length) return;

    var selectedFace = this.faceBtns.find(function (b) { return b.getAttribute('aria-selected') === 'true'; });
    this.face = (selectedFace && selectedFace.dataset.face) || 'obverse';
    this.index = 0;

    var self = this;
    this.faceBtns.forEach(function (b) { b.addEventListener('click', function () { self.setFace(b.dataset.face); }); });
    var faces = this.querySelector('.symbol-explorer__faces');
    if (faces) faces.addEventListener('keydown', function (e) {
      if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
      e.preventDefault();
      self.setFace(self.face === 'obverse' ? 'reverse' : 'obverse', true);
    });

    if (this.prev) this.prev.addEventListener('click', function () { self.scrollByAmount(-1); });
    if (this.next) this.next.addEventListener('click', function () { self.scrollByAmount(1); });

    var ticking = false;
    this.track.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () { self.updateScroll(); ticking = false; });
    }, { passive: true });

    this.track.addEventListener('keydown', function (e) {
      var ft = self.faceTabs(), last = ft.length - 1, i = self.index;
      if (e.key === 'ArrowRight') i = Math.min(last, i + 1);
      else if (e.key === 'ArrowLeft') i = Math.max(0, i - 1);
      else if (e.key === 'Home') i = 0;
      else if (e.key === 'End') i = last;
      else return;
      e.preventDefault();
      self.select(i, true);
    });

    // bind each tab once; resolve its index within its face at click time
    this.allTabs.forEach(function (t) {
      t.addEventListener('click', function () {
        if (t.dataset.face !== self.face) return;
        var i = self.faceTabs().indexOf(t);
        if (i >= 0) self.select(i, false);
      });
    });

    this._onResize = function () { self.positionThumb(); self.positionMarker(); self.updateScroll(); };
    addEventListener('resize', this._onResize);

    this.applyFace(true);
    requestAnimationFrame(function () { self.positionThumb(); self.positionMarker(); self.updateScroll(); });
  }

  disconnectedCallback() { if (this._onResize) removeEventListener('resize', this._onResize); }

  faceTabs() { var f = this.face; return this.allTabs.filter(function (t) { return t.dataset.face === f; }); }
  facePanels() { var f = this.face; return this.allPanels.filter(function (p) { return p.dataset.face === f; }); }

  scrollByAmount(dir) {
    // always native-smooth: the arrow nudge is a deliberate, gentle motion
    this.track.scrollBy({ left: dir * this.track.clientWidth * 0.7, behavior: 'smooth' });
  }

  setFace(face, focusTab) {
    if (!face || face === this.face) return;
    this.face = face;
    this.index = 0;
    this.faceBtns.forEach(function (b) { b.setAttribute('aria-selected', b.dataset.face === face ? 'true' : 'false'); });
    this.positionThumb();
    this.track.scrollTo({ left: 0, behavior: 'auto' });
    this.applyFace(false);
    var ft = this.faceTabs();
    if (focusTab && ft[0]) ft[0].focus();
  }

  applyFace(instant) {
    var f = this.face;
    this.allTabs.forEach(function (t) { t.hidden = t.dataset.face !== f; });
    this.select(0, false, instant);
    this.positionMarker();
    this.updateScroll();
    // re-measure once the just-unhidden tabs have laid out (marker + arrows)
    var self = this;
    requestAnimationFrame(function () { self.positionMarker(); self.updateScroll(); });
  }

  select(i, focus, instant) {
    var ft = this.faceTabs();
    if (!ft.length) return;
    this.index = i;
    ft.forEach(function (t, k) {
      var on = k === i;
      t.setAttribute('aria-selected', on ? 'true' : 'false');
      t.tabIndex = on ? 0 : -1;
    });
    this.positionMarker();
    this.centerTab(ft[i]);
    this.updateCount();
    this.showPanel(this.facePanels()[i], instant);
    if (focus) ft[i].focus();
  }

  showPanel(next, instant) {
    if (!next) return;
    var current = this.allPanels.find(function (p) { return !p.hidden && p !== next; });
    var self = this;
    if (current && !instant) {
      current.classList.add('is-swapping');
      clearTimeout(this._swap);
      this._swap = setTimeout(function () {
        current.hidden = true;
        current.classList.remove('is-swapping');
        self.reveal(next, false);
      }, 160);
    } else {
      this.allPanels.forEach(function (p) { if (p !== next) { p.hidden = true; p.classList.remove('is-swapping'); } });
      this.reveal(next, instant);
    }
  }

  reveal(panel, instant) {
    panel.hidden = false;
    if (instant) { panel.classList.remove('is-swapping'); return; }
    panel.classList.add('is-swapping');
    requestAnimationFrame(function () { requestAnimationFrame(function () { panel.classList.remove('is-swapping'); }); });
  }

  positionThumb() {
    var b = this.faceBtns.find(function (x) { return x.getAttribute('aria-selected') === 'true'; });
    if (!b || !this.thumb) return;
    this.thumb.style.width = b.offsetWidth + 'px';
    this.thumb.style.transform = 'translateX(' + b.offsetLeft + 'px)';
  }

  positionMarker() {
    var t = this.faceTabs()[this.index];
    if (!t || !this.marker) return;
    this.marker.style.width = t.offsetWidth + 'px';
    this.marker.style.transform = 'translateX(' + t.offsetLeft + 'px)';
  }

  centerTab(t) {
    if (!t) return;
    var target = t.offsetLeft - (this.track.clientWidth - t.offsetWidth) / 2;
    this.track.scrollTo({ left: Math.max(0, target), behavior: this.reduce ? 'auto' : 'smooth' });
  }

  updateScroll() {
    var el = this.track, max = el.scrollWidth - el.clientWidth;
    this.row.classList.toggle('can-left', el.scrollLeft > 1);
    this.row.classList.toggle('can-right', el.scrollLeft < max - 1);
  }

  updateCount() {
    if (!this.count) return;
    var n = this.faceTabs().length;
    this.count.textContent = String(this.index + 1).padStart(2, '0') + ' / ' + String(n).padStart(2, '0');
  }
}
customElements.define('symbol-explorer', SymbolExplorer);
